// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { patch, removeItem } from '@ngxs/store/operators';

// Services
import { UnitConfigService } from '../services/unit-config.service';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';
import { CoreService } from '../../../shared/services';
import { DialogService } from '../../../shared/services/dialog.service';

// Models
import { UnitConfigViewModel } from '../models/unit-config-view.model';
import { AppCleanup } from '../../../shared/models';
import { ConfirmDialogModel } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Store
import { UnitConfigActions } from './unit-config.action';

// Enums
import { StatusType } from '../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../shared/helpers';

export interface UnitConfigStateModel {
	items: UnitConfigViewModel[];
	loaded: boolean;
}

const defaultState: UnitConfigStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'unitConfig',
	defaults: defaultState,
})
@Injectable()
export class UnitConfigState extends AppCleanup {
	constructor(
		private unitConfigService: UnitConfigService,
		private spinnerService: SpinnerService,
		private translateService: TranslateService,
		private coreService: CoreService,
		private dialogService: DialogService
	) {
		super();
	}

	@Action(UnitConfigActions.GetAll)
	getAll(ctx: StateContext<UnitConfigStateModel>, action: UnitConfigActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.unitConfigService
			.getUnitConfigs()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (channels) => {
					ctx.setState({
						items: channels,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.UNIT_CONFIG.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UnitConfigActions.SetAll)
	setAll(ctx: StateContext<UnitConfigStateModel>, action: UnitConfigActions.SetAll) {
		ctx.setState({
			items: action.configs,
			loaded: true,
		});
	}

	@Action(UnitConfigActions.Delete)
	delete(ctx: StateContext<UnitConfigStateModel>, action: UnitConfigActions.Delete) {
		let deleteUnitConfig = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.unitConfigService
				.deleteUnitConfig(action.data.id)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: () => {
						const currentState = ctx.getState();
						const existingItem = currentState.items?.find((item) => item.id === action.data.id);

						if (existingItem) {
							ctx.setState(
								patch({
									items: removeItem((unitConfig) => unitConfig.id === action.data.id),
								})
							);
						}

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.UNIT_CONFIG.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.UNIT_CONFIG.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DELETE'))} ${this.translateService.instant('MODULE.UNIT_CONFIG.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.UNUT_CONFIG.TITLE') })}`,
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteUnitConfig();
					}
				});
		} else {
			deleteUnitConfig();
		}
	}

	@Selector()
	static isLoaded(state: UnitConfigStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static unitConfigs(state: UnitConfigStateModel): UnitConfigViewModel[] {
		return state.items;
	}
}
